import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Eye, Pencil, Trash2, Target, Phone, UserCheck, Users, Copy, Download, Upload, FileText, ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, CheckSquare, Square, X } from 'lucide-react'
import { leadsApi, usersApi, assignmentApi, metaApi, type Lead, type User, type ImportPreview, type ImportPreviewRow } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select, Textarea } from '../../components/ui/Input'
import PhoneInput from '../../components/ui/PhoneInput'
import { StageBadge, SegmentBadge } from '../../components/ui/Badge'
import { usePipelineStages } from '../../hooks/usePipelineStages'
import { fmtDate } from '../../utils/date'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import MobileFilterPanel from '../../components/ui/MobileFilterPanel'
import { useConfirm } from '../../components/ui/ConfirmDialog'

const FALLBACK_STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'demo_scheduled', label: 'Demo Scheduled' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

const SEGMENTS = [
  { value: '', label: 'All Segments' },
  { value: 'HOT', label: 'HOT' },
  { value: 'WARM', label: 'WARM' },
  { value: 'COLD', label: 'COLD' },
]

export default function LeadsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isSalesRep = currentUser?.role === 'sales_rep'
  const stagesFromApi = usePipelineStages()
  const STAGES = stagesFromApi.length > 0
    ? [{ value: '', label: 'All Stages' }, ...stagesFromApi.map(s => ({ value: s.slug, label: s.label }))]
    : FALLBACK_STAGES
  const [filters, setFilters] = useState({ stage: '', quality_segment: '', owner_id: '', meta_campaign_id: '', meta_ad_set_id: '', meta_ad_id: '', meta_form_id: '', search: '', created_from: '', created_to: '', updated_from: '', updated_to: '', submitted_from: '', submitted_to: '', sort_by: 'created_at', sort_dir: 'desc' })
  const [showDateFilters, setShowDateFilters] = useState(false)
  const [offset, setOffset] = useState(0)
  const limit = 25
  const [modalOpen, setModalOpen] = useState(false)
  const [editingLead, setEditingLead] = useState<Lead | null>(null)
  const [previewLeadId, setPreviewLeadId] = useState<string | number | null>(null)
  const [assigningLeadId, setAssigningLeadId] = useState<string | number | null>(null)
  const [duplicatesModalOpen, setDuplicatesModalOpen] = useState(false)
  const [csvImportOpen, setCsvImportOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false)
  const [confirmNode, confirmDialog] = useConfirm()

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const params: Record<string, string> = { limit: String(limit), offset: String(offset), sort_by: filters.sort_by, sort_dir: filters.sort_dir }
  if (filters.stage) params.stage = filters.stage
  if (filters.quality_segment) params.quality_segment = filters.quality_segment
  if (filters.owner_id) params.owner_id = filters.owner_id
  if (filters.meta_campaign_id) params.meta_campaign_id = filters.meta_campaign_id
  if (filters.meta_ad_set_id) params.meta_ad_set_id = filters.meta_ad_set_id
  if (filters.meta_ad_id) params.meta_ad_id = filters.meta_ad_id
  if (filters.meta_form_id) params.meta_form_id = filters.meta_form_id
  if (filters.search) params.search = filters.search
  if (filters.created_from) params.created_from = filters.created_from
  if (filters.created_to) params.created_to = filters.created_to
  if (filters.updated_from) params.updated_from = filters.updated_from
  if (filters.updated_to) params.updated_to = filters.updated_to
  if (filters.submitted_from) params.submitted_from = filters.submitted_from
  if (filters.submitted_to) params.submitted_to = filters.submitted_to

  const { data, isLoading } = useQuery({
    queryKey: ['leads', params],
    queryFn: () => leadsApi.list(params),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => leadsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  })

  const { data: usersData } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => usersApi.list(),
  })

  // Meta ad-level attribution filters (cascading: campaign → ad set → ad; form independent).
  // Graceful degradation: these calls fail when no Meta connection/page is configured, so we
  // disable retries and coalesce to [] — the dropdowns just render empty rather than crashing.
  // Gated to non-sales-rep: /meta/campaigns exposes budget/spend fields, and /meta/forms is a
  // LIVE Graph API sync (it re-fetches from Meta and upserts on every GET), so it must not be
  // triggered by every rep's Leads page load.
  const canFilterByAd = !isSalesRep

  const { data: campaignsData } = useQuery({
    queryKey: ['meta-campaigns'],
    queryFn: () => metaApi.listCampaigns(),
    enabled: canFilterByAd,
    retry: false,
    staleTime: 30 * 60 * 1000,
  })
  const campaigns = (campaignsData?.data ?? []) as Array<{ campaign_id: string; name: string | null }>

  const { data: adSetsData } = useQuery({
    queryKey: ['meta-adsets', filters.meta_campaign_id],
    queryFn: () => metaApi.listAdSets(filters.meta_campaign_id),
    enabled: canFilterByAd && !!filters.meta_campaign_id,
    retry: false,
    staleTime: 30 * 60 * 1000,
  })
  const adSets = (adSetsData?.data ?? []) as Array<{ ad_set_id: string; name: string | null }>

  const { data: adsData } = useQuery({
    queryKey: ['meta-ads', filters.meta_ad_set_id],
    queryFn: () => metaApi.listAds(filters.meta_ad_set_id),
    enabled: canFilterByAd && !!filters.meta_ad_set_id,
    retry: false,
    staleTime: 30 * 60 * 1000,
  })
  const ads = (adsData?.data ?? []) as Array<{ ad_id: string; name: string | null }>

  // Longest staleTime of the four — every fetch is an outbound Meta Graph call plus a DB write.
  const { data: formsData } = useQuery({
    queryKey: ['meta-forms'],
    queryFn: () => metaApi.listForms(),
    enabled: canFilterByAd,
    retry: false,
    staleTime: 60 * 60 * 1000,
  })
  const forms = (formsData?.data ?? []) as Array<{ form_id: string; form_name: string | null }>

  const [autoAssignResult, setAutoAssignResult] = useState<{ assigned: number; skipped: number } | null>(null)
  const autoAssignMutation = useMutation({
    mutationFn: () => assignmentApi.autoAssign(),
    onSuccess: (resp) => {
      setAutoAssignResult(resp.data)
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setTimeout(() => setAutoAssignResult(null), 5000)
    },
  })

  const { data: duplicatesData } = useQuery({
    queryKey: ['leads-duplicates'],
    queryFn: () => leadsApi.getDuplicates(),
  })
  const duplicateCount = duplicatesData?.data?.length || 0

  const leads = data?.leads || []
  const total = data?.total || 0
  const usersList = usersData?.users || []

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === leads.length && leads.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(leads.map((l: any) => l.id)))
    }
  }, [leads, selectedIds.size])

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {!isSalesRep && (
            <>
              <Button variant="secondary" onClick={() => setDuplicatesModalOpen(true)}>
                <Copy size={16} /> Duplicates
                {duplicateCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">{duplicateCount}</span>
                )}
              </Button>
              <Button variant="secondary" onClick={() => autoAssignMutation.mutate()} loading={autoAssignMutation.isPending}>
                <Users size={16} /> Auto-Assign
              </Button>
              <Button variant="secondary" onClick={() => {
                const exportParams: Record<string, string> = {}
                if (filters.search) exportParams.search = filters.search
                if (filters.stage) exportParams.stage = filters.stage
                if (filters.quality_segment) exportParams.segment = filters.quality_segment
                if (filters.owner_id) exportParams.owner_id = filters.owner_id
                // Keep in sync with the list filters — an export that ignores them
                // would hand back far more rows than the user is looking at.
                if (filters.meta_campaign_id) exportParams.meta_campaign_id = filters.meta_campaign_id
                if (filters.meta_ad_set_id) exportParams.meta_ad_set_id = filters.meta_ad_set_id
                if (filters.meta_ad_id) exportParams.meta_ad_id = filters.meta_ad_id
                if (filters.meta_form_id) exportParams.meta_form_id = filters.meta_form_id
                leadsApi.exportCsv(Object.keys(exportParams).length > 0 ? exportParams : undefined)
              }}>
                <Download size={16} /> Export
              </Button>
              <Button variant="secondary" onClick={() => setCsvImportOpen(true)}>
                <Upload size={16} /> Import
              </Button>
            </>
          )}
          <Button onClick={() => { setEditingLead(null); setModalOpen(true) }}>
            <Plus size={16} /> Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 space-y-3">
        <div className="relative w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, phone, company, or lead ID..."
            value={filters.search}
            onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setOffset(0) }}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <MobileFilterPanel activeCount={[filters.stage, filters.quality_segment, filters.owner_id, filters.meta_campaign_id, filters.meta_ad_set_id, filters.meta_ad_id, filters.meta_form_id].filter(Boolean).length + (filters.sort_by !== 'created_at' ? 1 : 0)}>
          <Select
            options={STAGES}
            value={filters.stage}
            onChange={(e) => { setFilters(f => ({ ...f, stage: e.target.value })); setOffset(0) }}
          />
          <Select
            options={SEGMENTS}
            value={filters.quality_segment}
            onChange={(e) => { setFilters(f => ({ ...f, quality_segment: e.target.value })); setOffset(0) }}
          />
          {!isSalesRep && (
            <Select
              options={[
                { value: '', label: 'All Agents' },
                ...usersList.map((u: User) => ({ value: String(u.id), label: u.full_name || u.username })),
              ]}
              value={filters.owner_id}
              onChange={(e) => { setFilters(f => ({ ...f, owner_id: e.target.value })); setOffset(0) }}
            />
          )}
          {canFilterByAd && (
            <>
              <Select
                options={[
                  { value: '', label: 'All Campaigns' },
                  ...campaigns.map(c => ({ value: c.campaign_id, label: c.name || c.campaign_id })),
                ]}
                value={filters.meta_campaign_id}
                onChange={(e) => { setFilters(f => ({ ...f, meta_campaign_id: e.target.value, meta_ad_set_id: '', meta_ad_id: '' })); setOffset(0) }}
              />
              <Select
                options={[
                  { value: '', label: 'All Ad Sets' },
                  ...adSets.map(a => ({ value: a.ad_set_id, label: a.name || a.ad_set_id })),
                ]}
                value={filters.meta_ad_set_id}
                disabled={!filters.meta_campaign_id}
                onChange={(e) => { setFilters(f => ({ ...f, meta_ad_set_id: e.target.value, meta_ad_id: '' })); setOffset(0) }}
              />
              <Select
                options={[
                  { value: '', label: 'All Ads' },
                  ...ads.map(a => ({ value: a.ad_id, label: a.name || a.ad_id })),
                ]}
                value={filters.meta_ad_id}
                disabled={!filters.meta_ad_set_id}
                onChange={(e) => { setFilters(f => ({ ...f, meta_ad_id: e.target.value })); setOffset(0) }}
              />
              <Select
                options={[
                  { value: '', label: 'All Forms' },
                  ...forms.map(fm => ({ value: fm.form_id, label: fm.form_name || fm.form_id })),
                ]}
                value={filters.meta_form_id}
                onChange={(e) => { setFilters(f => ({ ...f, meta_form_id: e.target.value })); setOffset(0) }}
              />
            </>
          )}
          <Select
            options={[
              { value: 'created_at', label: 'Sort: Created' },
              { value: 'updated_at', label: 'Sort: Updated' },
              { value: 'submitted_at', label: 'Sort: Submitted' },
              { value: 'last_call_date', label: 'Sort: Last Call' },
              { value: 'score', label: 'Sort: Score' },
              { value: 'name', label: 'Sort: Name' },
            ]}
            value={filters.sort_by}
            onChange={(e) => { setFilters(f => ({ ...f, sort_by: e.target.value })); setOffset(0) }}
          />
          <button
            onClick={() => setFilters(f => ({ ...f, sort_dir: f.sort_dir === 'desc' ? 'asc' : 'desc' }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            title={`Currently: ${filters.sort_dir === 'desc' ? 'Newest first' : 'Oldest first'}`}
          >
            {filters.sort_dir === 'desc' ? 'Desc' : 'Asc'}
          </button>
          <button
            onClick={() => setShowDateFilters(!showDateFilters)}
            className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
              showDateFilters || filters.created_from || filters.created_to || filters.updated_from || filters.updated_to || filters.submitted_from || filters.submitted_to
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Dates
          </button>
        </MobileFilterPanel>
      </div>

      {/* Date range filters (collapsible) */}
      {showDateFilters && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 whitespace-nowrap">Created:</span>
            <input
              type="date"
              value={filters.created_from}
              onChange={(e) => { setFilters(f => ({ ...f, created_from: e.target.value })); setOffset(0) }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              value={filters.created_to}
              onChange={(e) => { setFilters(f => ({ ...f, created_to: e.target.value })); setOffset(0) }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 whitespace-nowrap">Updated:</span>
            <input
              type="date"
              value={filters.updated_from}
              onChange={(e) => { setFilters(f => ({ ...f, updated_from: e.target.value })); setOffset(0) }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              value={filters.updated_to}
              onChange={(e) => { setFilters(f => ({ ...f, updated_to: e.target.value })); setOffset(0) }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 whitespace-nowrap">Submitted:</span>
            <input
              type="date"
              value={filters.submitted_from}
              onChange={(e) => { setFilters(f => ({ ...f, submitted_from: e.target.value })); setOffset(0) }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              value={filters.submitted_to}
              onChange={(e) => { setFilters(f => ({ ...f, submitted_to: e.target.value })); setOffset(0) }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          {(filters.created_from || filters.created_to || filters.updated_from || filters.updated_to || filters.submitted_from || filters.submitted_to) && (
            <button
              onClick={() => { setFilters(f => ({ ...f, created_from: '', created_to: '', updated_from: '', updated_to: '', submitted_from: '', submitted_to: '' })); setOffset(0) }}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Clear dates
            </button>
          )}
        </div>
      )}

      {/* Auto-assign result banner */}
      {autoAssignResult && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg flex items-center gap-2">
          <UserCheck size={16} />
          <span>{autoAssignResult.assigned} lead{autoAssignResult.assigned !== 1 ? 's' : ''} assigned, {autoAssignResult.skipped} skipped</span>
        </div>
      )}

      {/* Bulk action toolbar */}
      {selectedIds.size > 0 && !isSalesRep && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-3">
          <span className="text-sm font-medium text-indigo-700">{selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <Button size="sm" variant="primary" onClick={() => setBulkUpdateOpen(true)}>
            <Pencil size={14} /> Bulk Update
          </Button>
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <X size={14} /> Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <LoadingState message="Loading leads..." />
      ) : leads.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No leads found"
          description="Create your first lead or adjust filters"
          action={<Button onClick={() => { setEditingLead(null); setModalOpen(true) }}><Plus size={16} /> Add Lead</Button>}
        />
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-2">
            {leads.map((lead) => (
              <div
                key={lead.id}
                onClick={() => navigate(`/leads/${(lead as any).lead_id || lead.id}`)}
                className="bg-white rounded-xl border border-gray-200 p-4 active:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900">{lead.name}</span>
                      <SegmentBadge segment={lead.quality_segment} />
                    </div>
                    {lead.company && <p className="text-xs text-gray-500 mt-0.5">{lead.company}</p>}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <StageBadge stage={lead.stage} />
                      {lead.score != null && <span className="text-xs text-gray-500">Score: {Math.round(lead.score)}</span>}
                      {lead.owner_name || lead.owner ? (
                        <span className="text-xs text-gray-500">{String(lead.owner_name || lead.owner)}</span>
                      ) : (
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Unassigned</span>
                      )}
                      {(lead as any).sla_status && (lead as any).sla_status !== 'ok' && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          (lead as any).sla_status === 'breached' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          SLA {String((lead as any).sla_status).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  {lead.phone_number && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/voip?lead=${(lead as any).lead_id || lead.id}`) }}
                      className="ml-3 p-3 bg-green-600 text-white rounded-lg active:bg-green-700 flex-shrink-0"
                    >
                      <Phone size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <button onClick={toggleSelectAll} className="text-gray-400 hover:text-indigo-600">
                      {selectedIds.size === leads.length && leads.length > 0 ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase w-12">ID</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Stage</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Segment</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map((lead) => (
                  <tr key={lead.id} className={`hover:bg-gray-50 cursor-pointer ${selectedIds.has((lead as any).id) ? 'bg-indigo-50/50' : ''}`} onClick={() => navigate(`/leads/${(lead as any).lead_id || lead.id}`)}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleSelect((lead as any).id)} className="text-gray-400 hover:text-indigo-600">
                        {selectedIds.has((lead as any).id) ? <CheckSquare size={16} className="text-indigo-600" /> : <Square size={16} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400">{(lead as any).lead_id || lead.id}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                      {lead.title && <p className="text-xs text-gray-500">{lead.title}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{lead.company || '—'}</td>
                    <td className="px-4 py-3"><StageBadge stage={lead.stage} /></td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{lead.score != null ? Math.round(lead.score) : '—'}</span>
                    </td>
                    <td className="px-4 py-3"><SegmentBadge segment={lead.quality_segment} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {lead.owner_name || lead.owner ? (
                          <span className="text-xs text-gray-700">{String(lead.owner_name || lead.owner)}</span>
                        ) : (
                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Unassigned</span>
                        )}
                        {(lead as any).sla_status && (
                          <span
                            className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                              (lead as any).sla_status === 'ok' ? 'bg-green-500' :
                              (lead as any).sla_status === 'warning' ? 'bg-amber-500' :
                              (lead as any).sla_status === 'breached' ? 'bg-red-500' : 'bg-gray-300'
                            }`}
                            title={`SLA: ${String((lead as any).sla_status)}`}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-500">{lead.email || '—'}</p>
                      {lead.phone_number && <p className="text-xs text-gray-400">{lead.phone_number}</p>}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setPreviewLeadId((lead as any).lead_id || lead.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded" title="Quick view"><Eye size={15} /></button>
                        <button onClick={() => setAssigningLeadId((lead as any).lead_id || lead.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded" title="Assign"><UserCheck size={15} /></button>
                        {lead.phone_number && (
                          <button onClick={() => navigate(`/voip?lead=${(lead as any).lead_id || lead.id}`)} className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded" title="Call"><Phone size={15} /></button>
                        )}
                        <button onClick={() => { setEditingLead(lead); setModalOpen(true) }} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded" title="Edit"><Pencil size={15} /></button>
                        <button onClick={async () => { const ok = await confirmDialog({ message: 'Are you sure you want to delete this lead? This cannot be undone.', title: 'Delete Lead', confirmLabel: 'Delete', confirmVariant: 'danger' }); if (!ok) return; deleteMutation.mutate((lead as any).lead_id || lead.id) }} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination total={total} limit={limit} offset={offset} onChange={setOffset} />
        </>
      )}

      {/* Create/Edit Modal */}
      <LeadFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingLead(null) }}
        lead={editingLead}
      />

      {/* Quick View Modal */}
      {previewLeadId && (
        <QuickViewModal
          leadId={previewLeadId}
          onClose={() => setPreviewLeadId(null)}
          onViewFull={() => { setPreviewLeadId(null); navigate(`/leads/${previewLeadId}`) }}
        />
      )}

      {/* Quick Call Modal removed — calls are initiated from VoIP page */}

      {/* Assign Lead Modal */}
      {assigningLeadId && (
        <AssignLeadModal
          leadId={assigningLeadId}
          users={usersList}
          onClose={() => setAssigningLeadId(null)}
        />
      )}

      {/* Duplicates Modal */}
      <DuplicatesModal
        open={duplicatesModalOpen}
        onClose={() => setDuplicatesModalOpen(false)}
      />

      {/* CSV Import Modal */}
      <CsvImportModal
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['leads'] })}
      />

      {/* Bulk Update Modal */}
      <BulkUpdateModal
        open={bulkUpdateOpen}
        onClose={() => setBulkUpdateOpen(false)}
        selectedIds={Array.from(selectedIds)}
        onComplete={() => {
          setSelectedIds(new Set())
          queryClient.invalidateQueries({ queryKey: ['leads'] })
        }}
      />

      {confirmNode}
    </div>
  )
}

function LeadFormModal({ open, onClose, lead }: { open: boolean; onClose: () => void; lead: Lead | null }) {
  const queryClient = useQueryClient()
  const stagesFromApi = usePipelineStages()
  const STAGES = stagesFromApi.length > 0
    ? [{ value: '', label: 'All Stages' }, ...stagesFromApi.map(s => ({ value: s.slug, label: s.label }))]
    : FALLBACK_STAGES
  const [form, setForm] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone_number: lead?.phone_number || '',
    company: lead?.company || '',
    title: lead?.title || '',
    industry: lead?.industry || '',
    website: (lead as any)?.website || '',
    stage: lead?.stage || 'new',
    lead_source: (lead as any)?.lead_source || '',
    channel: (lead as any)?.channel || '',
    notes: (lead as any)?.notes || '',
    deal_value: lead?.deal_value != null ? String(lead.deal_value) : '',
    owner_id: (lead as any)?.owner_id != null ? String((lead as any).owner_id) : '',
  })

  // Fetch users for owner selector
  const { data: usersForOwner } = useQuery({
    queryKey: ['users-for-owner'],
    queryFn: () => usersApi.list(),
    enabled: open,
  })
  const userOptions = [
    { value: '', label: 'Unassigned' },
    ...((usersForOwner?.users || []).filter((u: any) => u.is_active).map((u: any) => ({
      value: String(u.id),
      label: u.full_name || u.username,
    }))),
  ]

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => lead ? leadsApi.update(lead.id, data) : leadsApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); onClose() },
  })

  // If owner changed on an existing lead, also call the assign endpoint for audit trail
  const assignMutation = useMutation({
    mutationFn: ({ leadId, userId }: { leadId: number; userId: number }) =>
      leadsApi.assignLead(leadId, { assigned_to: userId, reason: 'Reassigned via lead edit' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: Record<string, unknown> = { ...form }
    if (form.deal_value) data.deal_value = parseFloat(form.deal_value)
    else delete data.deal_value

    // Handle owner assignment
    const newOwnerId = form.owner_id ? parseInt(form.owner_id) : null
    const oldOwnerId = (lead as any)?.owner_id || null

    if (lead) {
      // Edit mode: don't send owner_id in the update — use assign endpoint for audit trail
      delete data.owner_id
    } else {
      // Create mode: include owner_id so backend assigns at creation
      if (newOwnerId) data.owner_id = newOwnerId
      else delete data.owner_id
    }

    Object.keys(data).forEach(k => { if (data[k] === '' && k !== 'name' && k !== 'stage') delete data[k] })

    createMutation.mutate(data, {
      onSuccess: () => {
        // If owner changed on an existing lead, fire the assign endpoint
        if (lead && newOwnerId && newOwnerId !== oldOwnerId) {
          assignMutation.mutate({ leadId: lead.id, userId: newOwnerId })
        }
      },
    })
  }

  // Reset form when lead changes
  if (open && lead && form.name !== lead.name) {
    setForm({
      name: lead.name || '',
      email: lead.email || '',
      phone_number: lead.phone_number || '',
      company: lead.company || '',
      title: lead.title || '',
      industry: lead.industry || '',
      website: (lead as any)?.website || '',
      stage: lead.stage || 'new',
      lead_source: (lead as any)?.lead_source || '',
      channel: (lead as any)?.channel || '',
      notes: (lead as any)?.notes || '',
      deal_value: lead.deal_value != null ? String(lead.deal_value) : '',
      owner_id: (lead as any)?.owner_id != null ? String((lead as any).owner_id) : '',
    })
  }

  return (
    <Modal open={open} onClose={onClose} title={lead ? 'Edit Lead' : 'Add Lead'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {createMutation.error && (
          <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{(createMutation.error as Error).message}</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
          <PhoneInput label="Phone" value={form.phone_number} onChange={(val) => setForm(f => ({ ...f, phone_number: val }))} />
          <Input label="Company" value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} />
          <Input label="Title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
          <Input label="Industry" value={form.industry} onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))} />
          <Input label="Website" value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} />
          <Input label="Est. Contract Value (INR)" type="number" value={form.deal_value} onChange={(e) => setForm(f => ({ ...f, deal_value: e.target.value }))} />
          <Select label="Stage" options={STAGES.filter(s => s.value)} value={form.stage} onChange={(e) => setForm(f => ({ ...f, stage: e.target.value }))} />
          <Input label="Lead Source" placeholder="e.g. Meta Ads, Referral, Website" value={form.lead_source} onChange={(e) => setForm(f => ({ ...f, lead_source: e.target.value }))} />
          <Select
            label="Channel"
            options={[
              { value: '', label: 'Select Channel' },
              { value: 'manual_ui', label: 'Manual' },
              { value: 'csv_import', label: 'CSV Import' },
              { value: 'meta_webhook', label: 'Meta Webhook' },
              { value: 'meta_sync', label: 'Meta Sync' },
              { value: 'api', label: 'API' },
              { value: 'referral', label: 'Referral' },
              { value: 'organic', label: 'Organic' },
              { value: 'paid_social', label: 'Paid Social' },
              { value: 'paid_search', label: 'Paid Search' },
              { value: 'cold_outreach', label: 'Cold Outreach' },
            ]}
            value={form.channel}
            onChange={(e) => setForm(f => ({ ...f, channel: e.target.value }))}
          />
          <Select label="Assigned To" options={userOptions} value={form.owner_id} onChange={(e) => setForm(f => ({ ...f, owner_id: e.target.value }))} />
        </div>
        <Textarea label="Notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={createMutation.isPending}>{lead ? 'Save Changes' : 'Create Lead'}</Button>
        </div>
      </form>
    </Modal>
  )
}

function QuickViewModal({ leadId, onClose, onViewFull }: { leadId: string | number; onClose: () => void; onViewFull: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsApi.get(leadId),
  })

  const lead = data?.data as any

  return (
    <Modal open={true} onClose={onClose} title={lead?.name || 'Lead'} size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center h-32 text-gray-400">Loading...</div>
      ) : !lead ? (
        <p className="text-gray-500">Lead not found</p>
      ) : (
        <div className="space-y-6">
          {/* Contact Info */}
          <section>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contact</h4>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Email</p>
                <p className="text-sm text-gray-900">{lead.email || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Phone</p>
                <p className="text-sm text-gray-900">{lead.phone_number || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Company</p>
                <p className="text-sm text-gray-900">{lead.company || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Industry</p>
                <p className="text-sm text-gray-900">{lead.industry || '—'}</p>
              </div>
            </div>
          </section>

          {/* Stage + Score */}
          <section className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <StageBadge stage={lead.stage} />
              <SegmentBadge segment={lead.quality_segment} />
            </div>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{lead.score != null ? Math.round(lead.score) : '—'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Total Score</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-blue-600">{lead.fit_score ?? '—'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Fit (0-40)</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-green-600">{lead.engagement_score ?? '—'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Engagement (0-35)</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-amber-600">{lead.urgency_score ?? '—'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Urgency (0-25)</p>
              </div>
            </div>
          </section>

          {/* Deal + Activity Info */}
          <section>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Details</h4>
            <div className="grid grid-cols-3 gap-x-8 gap-y-3">
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Est. Value</p>
                <p className="text-sm text-gray-900">{lead.deal_value ? `₹${Number(lead.deal_value).toLocaleString()}` : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Source</p>
                <p className="text-sm text-gray-900">{lead.lead_source || lead.source || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 mb-0.5">Total Calls</p>
                <p className="text-sm text-gray-900">{lead.call_count || 0}</p>
              </div>
            </div>
          </section>

          {/* Stage History */}
          {lead.stage_history?.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Stage History</h4>
              <div className="space-y-2">
                {lead.stage_history.slice(0, 5).map((h: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className="text-xs text-gray-400 w-32 flex-shrink-0">{fmtDate(h.changed_at)}</span>
                    {h.from_stage && <StageBadge stage={h.from_stage} />}
                    <span className="text-gray-300">→</span>
                    <StageBadge stage={h.to_stage} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Notes */}
          {lead.notes && (
            <section>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Notes</h4>
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{lead.notes}</p>
            </section>
          )}

          {/* Action */}
          <div className="pt-3 border-t border-gray-100">
            <Button variant="secondary" onClick={onViewFull} className="w-full">
              View Full Details →
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

function AssignLeadModal({ leadId, users, onClose }: { leadId: string | number; users: User[]; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [reason, setReason] = useState('')

  const assignMutation = useMutation({
    mutationFn: (data: { assigned_to: number; reason?: string }) => leadsApi.assignLead(leadId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      onClose()
    },
  })

  const handleAssign = () => {
    if (!selectedUserId) return
    assignMutation.mutate({ assigned_to: selectedUserId, ...(reason ? { reason } : {}) })
  }

  return (
    <Modal open={true} onClose={onClose} title="Assign Lead" size="sm">
      <div className="space-y-4">
        {assignMutation.error && (
          <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{(assignMutation.error as Error).message}</div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select team member</label>
          <select
            value={selectedUserId ?? ''}
            onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Choose a rep...</option>
            {users.filter(u => u.is_active).map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Product expertise match"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAssign} loading={assignMutation.isPending} disabled={!selectedUserId}>
            <UserCheck size={14} /> Assign
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function DuplicatesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['lead-duplicates'],
    queryFn: () => leadsApi.getDuplicates(),
    enabled: open,
  })

  const duplicates = data?.data || []

  return (
    <Modal open={open} onClose={onClose} title={`Potential Duplicates (${duplicates.length})`} size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center h-32 text-gray-400">Loading...</div>
      ) : duplicates.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-gray-500">No potential duplicates found.</p>
          <p className="text-xs text-gray-400 mt-1">The system checks for matching emails and phone numbers across leads.</p>
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
          {duplicates.map((dup: any, idx: number) => (
            <div key={idx} className="py-3 px-1">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{dup.lead_a_name || 'Unknown'}</span>
                    <span className="text-xs font-mono text-gray-400">{dup.lead_a_public_id}</span>
                    {dup.lead_a_company && <span className="text-xs text-gray-500">({dup.lead_a_company})</span>}
                  </div>
                </div>
                <div className="px-3">
                  <span className="px-2 py-0.5 bg-orange-50 text-orange-700 text-[10px] font-semibold rounded-full border border-orange-200">
                    {dup.match_type === 'phone' ? 'Same Phone' : 'Same Email'}
                  </span>
                </div>
                <div className="flex-1 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-sm font-medium text-gray-900">{dup.lead_b_name || 'Unknown'}</span>
                    <span className="text-xs font-mono text-gray-400">{dup.lead_b_public_id}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500">Match: {dup.match_value}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onClose(); navigate(`/leads/${dup.lead_a_public_id || dup.lead_id_a}`) }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    View {dup.lead_a_public_id}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => { onClose(); navigate(`/leads/${dup.lead_b_public_id || dup.lead_id_b}`) }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    View {dup.lead_b_public_id}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

const SYSTEM_FIELDS = [
  { value: '', label: '-- Skip --' },
  { value: 'name', label: 'Name *', required: true },
  { value: 'email', label: 'Email' },
  { value: 'phone_number', label: 'Phone Number' },
  { value: 'company', label: 'Company' },
  { value: 'title', label: 'Title' },
  { value: 'industry', label: 'Industry' },
  { value: 'company_size', label: 'Company Size' },
  { value: 'website', label: 'Website' },
  { value: 'business_type', label: 'Business Type' },
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'notes', label: 'Notes' },
]

const COLUMN_ALIASES: Record<string, string> = {
  'name': 'name',
  'full name': 'name',
  'contact name': 'name',
  'email': 'email',
  'email address': 'email',
  'e-mail': 'email',
  'phone': 'phone_number',
  'phone number': 'phone_number',
  'phone_number': 'phone_number',
  'mobile': 'phone_number',
  'telephone': 'phone_number',
  'company': 'company',
  'company name': 'company',
  'organization': 'company',
  'title': 'title',
  'job title': 'title',
  'position': 'title',
  'industry': 'industry',
  'company size': 'company_size',
  'company_size': 'company_size',
  'size': 'company_size',
  'website': 'website',
  'url': 'website',
  'web': 'website',
  'business type': 'business_type',
  'business_type': 'business_type',
  'lead source': 'lead_source',
  'lead_source': 'lead_source',
  'source': 'lead_source',
  'notes': 'notes',
  'note': 'notes',
  'comments': 'notes',
}

function parseCsvHeaders(text: string): { headers: string[]; rowCount: number } {
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '')
  if (lines.length === 0) return { headers: [], rowCount: 0 }
  const headerLine = lines[0]
  const headers: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < headerLine.length; i++) {
    const ch = headerLine[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      headers.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  headers.push(current.trim())
  return { headers, rowCount: lines.length - 1 }
}

function suggestMapping(csvColumn: string): string {
  const normalized = csvColumn.toLowerCase().trim()
  return COLUMN_ALIASES[normalized] || ''
}

function CsvImportModal({ open, onClose, onComplete }: { open: boolean; onClose: () => void; onComplete: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [file, setFile] = useState<File | null>(null)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({})
  const [previewing, setPreviewing] = useState(false)
  const [previewData, setPreviewData] = useState<ImportPreview | null>(null)
  const [previewFilter, setPreviewFilter] = useState<'all' | 'valid' | 'invalid' | 'duplicate'>('all')
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ created: number; duplicates: number; errors: string[] } | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB
  const MAX_ROW_COUNT = 10000

  const resetState = () => {
    setStep(1)
    setFile(null)
    setCsvHeaders([])
    setRowCount(0)
    setColumnMapping({})
    setPreviewing(false)
    setPreviewData(null)
    setPreviewFilter('all')
    setImporting(false)
    setResult(null)
    setImportError(null)
    setFileError(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    setFileError(null)
    setImportError(null)

    if (selected.size > MAX_FILE_SIZE) {
      setFileError(`File is too large (${(selected.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 5 MB.`)
      setFile(null)
      setCsvHeaders([])
      setRowCount(0)
      return
    }

    setFile(selected)

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const { headers, rowCount: count } = parseCsvHeaders(text)

      if (count > MAX_ROW_COUNT) {
        setFileError(`File has ${count.toLocaleString()} rows. Maximum allowed is ${MAX_ROW_COUNT.toLocaleString()} rows.`)
        setFile(null)
        setCsvHeaders([])
        setRowCount(0)
        return
      }

      setCsvHeaders(headers)
      setRowCount(count)

      const mapping: Record<string, string> = {}
      headers.forEach(h => {
        mapping[h] = suggestMapping(h)
      })
      setColumnMapping(mapping)
    }
    reader.readAsText(selected)
  }

  const handleMappingChange = (csvCol: string, systemField: string) => {
    setColumnMapping(prev => ({ ...prev, [csvCol]: systemField }))
  }

  const nameIsMapped = Object.values(columnMapping).includes('name')

  const getActiveMapping = (): Record<string, string> => {
    const activeMapping: Record<string, string> = {}
    Object.entries(columnMapping).forEach(([csv, sys]) => {
      if (sys) activeMapping[csv] = sys
    })
    return activeMapping
  }

  const handlePreview = async () => {
    if (!file) return
    setPreviewing(true)
    setImportError(null)

    try {
      const resp = await leadsApi.previewImportCsv(file, getActiveMapping())
      setPreviewData(resp.data)
      setPreviewFilter('all')
      setStep(3)
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : 'Preview failed. Please check your file and try again.')
    } finally {
      setPreviewing(false)
    }
  }

  const handleImport = async () => {
    if (!file) return
    setImporting(true)
    setImportError(null)

    try {
      const resp = await leadsApi.importCsv(file, getActiveMapping())
      setResult(resp.data)
      setStep(4)
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : 'Import failed. Please check your file and try again.')
    } finally {
      setImporting(false)
    }
  }

  const handleDone = () => {
    onComplete()
    handleClose()
  }

  const filteredRows: ImportPreviewRow[] = previewData
    ? previewFilter === 'all'
      ? previewData.rows
      : previewData.rows.filter(r => r.status === previewFilter)
    : []

  const stepLabels: Record<number, string> = {
    1: 'Upload',
    2: 'Map Columns',
    3: 'Preview',
    4: 'Results',
  }

  const stepTitle = `Import Leads - ${stepLabels[step]}`

  return (
    <Modal open={open} onClose={handleClose} title={stepTitle} size="lg">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {([1, 2, 3, 4] as const).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
              s <= step ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {s < step ? <CheckCircle2 size={14} /> : s}
            </div>
            <span className={`text-xs ${s === step ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
              {stepLabels[s]}
            </span>
            {s < 4 && <div className="w-8 h-px bg-gray-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="space-y-5">
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <FileText size={32} className="mx-auto text-gray-400 mb-3" />
            {file ? (
              <div>
                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {csvHeaders.length} column{csvHeaders.length !== 1 ? 's' : ''} detected, {rowCount} row{rowCount !== 1 ? 's' : ''} of data
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600">Click to select a CSV file</p>
                <p className="text-xs text-gray-400 mt-1">Only .csv files are accepted (max 5 MB, 10,000 rows)</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {fileError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{fileError}</span>
            </div>
          )}

          <button
            type="button"
            onClick={() => leadsApi.downloadTemplate()}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1.5"
          >
            <Download size={14} />
            Download CSV template
          </button>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button onClick={() => setStep(2)} disabled={!file || csvHeaders.length === 0}>
              Next <ArrowRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 2 && (
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            Match each column from your CSV to the corresponding lead field. Columns mapped to "-- Skip --" will be ignored.
          </p>

          {importError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{importError}</span>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-2 gap-0 bg-gray-50 border-b border-gray-200 px-4 py-2.5">
              <span className="text-xs font-medium text-gray-500 uppercase">CSV Column</span>
              <span className="text-xs font-medium text-gray-500 uppercase">Maps To</span>
            </div>
            <div className="divide-y divide-gray-100 max-h-[40vh] overflow-y-auto">
              {csvHeaders.map((header) => (
                <div key={header} className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center px-4 py-2.5">
                  <span className="text-sm text-gray-800 font-mono truncate" title={header}>{header}</span>
                  <select
                    value={columnMapping[header] || ''}
                    onChange={(e) => handleMappingChange(header, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {SYSTEM_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {!nameIsMapped && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5">
              <AlertCircle size={14} />
              The "Name" field is required. Please map at least one column to Name.
            </p>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="secondary" onClick={() => setStep(1)}>
              <ArrowLeft size={14} /> Back
            </Button>
            <Button onClick={handlePreview} loading={previewing} disabled={!nameIsMapped}>
              Next <ArrowRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 3 && previewData && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-green-700">{previewData.valid_count}</p>
              <p className="text-xs text-green-600 mt-0.5">Valid</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-red-700">{previewData.invalid_count}</p>
              <p className="text-xs text-red-600 mt-0.5">Invalid</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-amber-700">{previewData.duplicate_count}</p>
              <p className="text-xs text-amber-600 mt-0.5">Duplicates</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-gray-500">{previewData.skipped_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">Skipped</p>
            </div>
          </div>

          {/* Warnings */}
          {previewData.invalid_count > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{previewData.invalid_count} row{previewData.invalid_count !== 1 ? 's have' : ' has'} validation errors and will be skipped.</span>
            </div>
          )}
          {previewData.duplicate_count > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{previewData.duplicate_count} row{previewData.duplicate_count !== 1 ? 's match' : ' matches'} existing leads and will be skipped.</span>
            </div>
          )}

          {/* Summary line */}
          <p className="text-sm text-gray-700 font-medium">
            {previewData.valid_count} lead{previewData.valid_count !== 1 ? 's' : ''} will be created.{' '}
            {previewData.total_rows - previewData.valid_count > 0 && (
              <span className="text-gray-500 font-normal">
                {previewData.total_rows - previewData.valid_count} will be skipped.
              </span>
            )}
          </p>

          {/* Filter tabs */}
          <div className="flex gap-1 border-b border-gray-200">
            {([
              { key: 'all' as const, label: 'All', count: previewData.total_rows },
              { key: 'valid' as const, label: 'Valid', count: previewData.valid_count },
              { key: 'invalid' as const, label: 'Invalid', count: previewData.invalid_count },
              { key: 'duplicate' as const, label: 'Duplicates', count: previewData.duplicate_count },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setPreviewFilter(tab.key)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  previewFilter === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Preview table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-[35vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase w-12">Row</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase w-20">Status</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">
                        No rows match this filter.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr key={row.row_number} className={
                        row.status === 'invalid' ? 'bg-red-50/50' :
                        row.status === 'duplicate' ? 'bg-amber-50/50' :
                        row.status === 'skipped' ? 'bg-gray-50/50' : ''
                      }>
                        <td className="px-3 py-2 text-xs text-gray-400 font-mono">{row.row_number}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                            row.status === 'valid' ? 'bg-green-100 text-green-700' :
                            row.status === 'invalid' ? 'bg-red-100 text-red-700' :
                            row.status === 'duplicate' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 truncate max-w-[140px]" title={String(row.cleaned_data?.name || '')}>
                          {String(row.cleaned_data?.name || '--')}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 truncate max-w-[120px]" title={String(row.cleaned_data?.phone_number || '')}>
                          {String(row.cleaned_data?.phone_number || '--')}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 truncate max-w-[140px]" title={String(row.cleaned_data?.email || '')}>
                          {String(row.cleaned_data?.email || '--')}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px]">
                          {row.status === 'invalid' && row.errors.length > 0 && (
                            <div className="space-y-0.5">
                              {row.errors.map((err, i) => (
                                <p key={i} className="text-red-600">
                                  <span className="font-medium">{err.field}:</span> {err.message}
                                </p>
                              ))}
                            </div>
                          )}
                          {row.status === 'duplicate' && row.duplicate_info && (
                            <p className="text-amber-600">
                              Duplicate of {row.duplicate_info.lead_id || 'row ' + row.duplicate_info.row_number} — {row.duplicate_info.name}, matched on {row.duplicate_info.match_field}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {importError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{importError}</span>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="secondary" onClick={() => { setStep(2); setImportError(null) }}>
              <ArrowLeft size={14} /> Back
            </Button>
            <Button onClick={handleImport} loading={importing} disabled={previewData.valid_count === 0}>
              <Upload size={14} /> Import {previewData.valid_count} Valid Lead{previewData.valid_count !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && result && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{result.created}</p>
              <p className="text-xs text-green-600 mt-1">Leads Created</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{result.duplicates}</p>
              <p className="text-xs text-amber-600 mt-1">Duplicates Skipped</p>
            </div>
            <div className={`border rounded-lg p-4 text-center ${result.errors.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-2xl font-bold ${result.errors.length > 0 ? 'text-red-700' : 'text-gray-400'}`}>{result.errors.length}</p>
              <p className={`text-xs mt-1 ${result.errors.length > 0 ? 'text-red-600' : 'text-gray-500'}`}>Errors</p>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-medium text-red-800 mb-2">Error details:</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-700">{err}</p>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleDone}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  )
}


function BulkUpdateModal({ open, onClose, selectedIds, onComplete }: {
  open: boolean
  onClose: () => void
  selectedIds: number[]
  onComplete: () => void
}) {
  const stagesFromApi = usePipelineStages()
  const STAGES = stagesFromApi.length > 0
    ? [{ value: '', label: 'All Stages' }, ...stagesFromApi.map(s => ({ value: s.slug, label: s.label }))]
    : FALLBACK_STAGES
  const [form, setForm] = useState({ lead_source: '', channel: '', stage: '' })
  const [result, setResult] = useState<{ updated: number; not_found: number } | null>(null)

  const bulkMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => leadsApi.bulkUpdate(data as any),
    onSuccess: (resp) => {
      setResult(resp.data)
      onComplete()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: Record<string, unknown> = { lead_ids: selectedIds }
    if (form.lead_source) payload.lead_source = form.lead_source
    if (form.lead_source) payload.source = form.lead_source
    if (form.channel) payload.channel = form.channel
    if (form.stage) payload.stage = form.stage
    bulkMutation.mutate(payload)
  }

  const handleClose = () => {
    setForm({ lead_source: '', channel: '', stage: '' })
    setResult(null)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title={`Bulk Update ${selectedIds.length} Lead${selectedIds.length !== 1 ? 's' : ''}`}>
      {result ? (
        <div className="space-y-3">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
            <CheckCircle2 size={16} />
            <span>{result.updated} lead{result.updated !== 1 ? 's' : ''} updated</span>
          </div>
          {result.not_found > 0 && (
            <p className="text-xs text-amber-600">{result.not_found} lead(s) not found</p>
          )}
          <div className="flex justify-end">
            <Button onClick={handleClose}>Done</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-500">Only filled fields will be updated. Leave blank to skip.</p>

          <Input
            label="Lead Source"
            placeholder="e.g. Meta Ads, Referral, Website, Cold Call"
            value={form.lead_source}
            onChange={(e) => setForm(f => ({ ...f, lead_source: e.target.value }))}
          />

          <Select
            label="Channel"
            options={[
              { value: '', label: '— Keep current —' },
              { value: 'manual_ui', label: 'Manual' },
              { value: 'csv_import', label: 'CSV Import' },
              { value: 'meta_webhook', label: 'Meta Webhook' },
              { value: 'meta_sync', label: 'Meta Sync' },
              { value: 'api', label: 'API' },
              { value: 'referral', label: 'Referral' },
              { value: 'organic', label: 'Organic' },
              { value: 'paid_social', label: 'Paid Social' },
              { value: 'paid_search', label: 'Paid Search' },
              { value: 'cold_outreach', label: 'Cold Outreach' },
            ]}
            value={form.channel}
            onChange={(e) => setForm(f => ({ ...f, channel: e.target.value }))}
          />

          <Select
            label="Stage"
            options={[
              { value: '', label: '— Keep current —' },
              ...STAGES.filter(s => s.value),
            ]}
            value={form.stage}
            onChange={(e) => setForm(f => ({ ...f, stage: e.target.value }))}
          />

          {bulkMutation.isError && (
            <p className="text-sm text-red-600">Failed to update leads. Please try again.</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button type="submit" loading={bulkMutation.isPending}
              disabled={!form.lead_source && !form.channel && !form.stage}>
              Update {selectedIds.length} Lead{selectedIds.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}

